import React, { useState } from 'react';
import { useDaggerheartData } from '../context/DataContext';

/**
 * EquipmentBrowser Component
 * Comprehensive equipment browser for viewing and potentially acquiring
 * all Daggerheart weapons and armor across all tiers.
 *
 * @param {object} props - The component props.
 * @param {object} props.character - The current character data object.
 * @param {function} props.updateCharacter - Function to update the character state.
 * @param {function} props.onManagerClose - Function called when closing the manager.
 */
const EquipmentBrowser = ({ character, updateCharacter, onManagerClose }) => {
  const allData = useDaggerheartData();
  const [activeTab, setActiveTab] = useState('primary');
  const [selectedTier, setSelectedTier] = useState('tier1');
  const [selectedType, setSelectedType] = useState('physical');
  const [selectedItem, setSelectedItem] = useState(null);

  // Early return if data is not loaded
  if (!allData || !allData.equipmentDatabase) {
    return <div>Loading equipment database...</div>;
  }

  const { equipmentDatabase } = allData;

  const renderWeaponCard = (weapon) => {
    return (
      <div 
        key={weapon.id} 
        className="p-4 border rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 border-gray-300 dark:border-gray-600"
        onClick={() => setSelectedItem(weapon)}
      >
        <div className="flex justify-between items-start mb-2">
          <h3 className="font-bold text-lg">{weapon.name}</h3>
          <span className={`px-2 py-1 text-xs rounded-full ${
            weapon.tier === 1 ? 'bg-gray-100 text-gray-800' :
            weapon.tier === 2 ? 'bg-green-100 text-green-800' :
            weapon.tier === 3 ? 'bg-blue-100 text-blue-800' :
            'bg-purple-100 text-purple-800'
          }`}>
            Tier {weapon.tier}
          </span>
        </div>
        
        <div className="space-y-1 text-sm">
          <p><strong>Trait:</strong> {weapon.trait}</p>
          <p><strong>Range:</strong> {weapon.range}</p>
          <p><strong>Damage:</strong> {weapon.damage}</p>
          <p><strong>Burden:</strong> {weapon.burden}</p>
          {weapon.type === 'magic' && (
            <p className="text-purple-600 dark:text-purple-400"><strong>Requires Spellcast</strong></p>
          )}
        </div>
        
        {weapon.feature && (
          <div className="mt-2 p-2 bg-yellow-50 dark:bg-yellow-900/20 rounded text-sm">
            <strong>Feature:</strong> {weapon.feature}
          </div>
        )}
      </div>
    );
  };

  const renderArmorCard = (armor) => {
    return (
      <div 
        key={armor.id} 
        className="p-4 border rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 border-gray-300 dark:border-gray-600"
        onClick={() => setSelectedItem(armor)}
      >
        <div className="flex justify-between items-start mb-2">
          <h3 className="font-bold text-lg">{armor.name}</h3>
          <span className={`px-2 py-1 text-xs rounded-full ${
            armor.tier === 1 ? 'bg-gray-100 text-gray-800' :
            armor.tier === 2 ? 'bg-green-100 text-green-800' :
            armor.tier === 3 ? 'bg-blue-100 text-blue-800' :
            'bg-purple-100 text-purple-800'
          }`}>
            Tier {armor.tier}
          </span>
        </div>
        
        <div className="space-y-1 text-sm">
          <p><strong>Base Thresholds:</strong> {armor.baseThresholds.major} / {armor.baseThresholds.severe}</p>
          <p><strong>Base Score:</strong> {armor.baseScore}</p>
        </div>
        
        {armor.feature && (
          <div className="mt-2 p-2 bg-yellow-50 dark:bg-yellow-900/20 rounded text-sm">
            <strong>Feature:</strong> {armor.feature}
          </div>
        )}
      </div>
    );
  };

  const addToInventory = (item) => {
    // Clean the item data to remove undefined values that Firebase can't handle
    const cleanItem = Object.fromEntries(
      Object.entries(item).filter(([key, value]) => value !== undefined)
    );
    
    // Determine item type based on which category it belongs to
    let itemType = 'equipment'; // default
    
    // Check if it's a primary weapon
    if (equipmentDatabase.primaryWeapons) {
      for (const tierKey in equipmentDatabase.primaryWeapons) {
        const tier = equipmentDatabase.primaryWeapons[tierKey];
        // Check in physical and magic subcategories
        const itemKey = cleanItem.id.replace('_t' + cleanItem.tier, '');
        if ((tier.physical && tier.physical[itemKey]) || (tier.magic && tier.magic[itemKey])) {
          itemType = 'primaryWeapons';
          break;
        }
      }
    }
    
    // Check if it's a secondary weapon (only if not already identified as primary)
    if (itemType === 'equipment' && equipmentDatabase.secondaryWeapons) {
      for (const tierKey in equipmentDatabase.secondaryWeapons) {
        const tier = equipmentDatabase.secondaryWeapons[tierKey];
        const itemKey = cleanItem.id.replace('_t' + cleanItem.tier, '');
        if (tier[itemKey]) {
          itemType = 'secondaryWeapons';
          break;
        }
      }
    }
    
    // Check if it's armor (only if not already identified)
    if (itemType === 'equipment' && equipmentDatabase.armor) {
      for (const tierKey in equipmentDatabase.armor) {
        const tier = equipmentDatabase.armor[tierKey];
        const itemKey = cleanItem.id.replace('_t' + cleanItem.tier, '');
        if (tier[itemKey]) {
          itemType = 'armor';
          break;
        }
      }
    }
    
    // Add the type to the clean item
    cleanItem.type = itemType;
    
    // Check if item already exists in inventory
    const currentEquipment = character.inventory?.equipment || [];
    const existingItemIndex = currentEquipment.findIndex(invItem => invItem.id === cleanItem.id);
    
    let updatedEquipment;
    if (existingItemIndex >= 0) {
      // Item exists, increment quantity
      updatedEquipment = [...currentEquipment];
      updatedEquipment[existingItemIndex] = {
        ...updatedEquipment[existingItemIndex],
        quantity: (updatedEquipment[existingItemIndex].quantity || 1) + 1
      };
    } else {
      // New item, add with quantity 1
      updatedEquipment = [...currentEquipment, { ...cleanItem, quantity: 1 }];
    }
    
    updateCharacter({
      ...character,
      inventory: {
        equipment: updatedEquipment,
        items: character.inventory?.items || [],
        ingredients: character.inventory?.ingredients || []
      }
    });
    setSelectedItem(null);
  };

  const getFilteredItems = () => {
    if (activeTab === 'primary') {
      const items = Object.values(equipmentDatabase.primaryWeapons).filter(weapon => {
        const tierMatch = !selectedTier || weapon.tier === parseInt(selectedTier.replace('tier', ''));
        const typeMatch = !selectedType || weapon.category === selectedType;
        return tierMatch && typeMatch;
      });
      return items;
    } else if (activeTab === 'secondary') {
      const items = Object.values(equipmentDatabase.secondaryWeapons).filter(weapon => {
        const tierMatch = !selectedTier || weapon.tier === parseInt(selectedTier.replace('tier', ''));
        return tierMatch;
      });
      return items;
    } else if (activeTab === 'armor') {
      const items = Object.values(equipmentDatabase.armor).filter(armor => {
        const tierMatch = !selectedTier || armor.tier === parseInt(selectedTier.replace('tier', ''));
        return tierMatch;
      });
      return items;
    }
    return [];
  };

  const filteredItems = getFilteredItems();

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Equipment Database</h2>
        <button 
          onClick={onManagerClose}
          className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
        >
          Close
        </button>
      </div>

      {/* Equipment Type Tabs */}
      <div className="flex space-x-1 border-b border-gray-300 dark:border-gray-600">
        {[
          { key: 'primary', label: 'Primary Weapons' },
          { key: 'secondary', label: 'Secondary Weapons' },
          { key: 'armor', label: 'Armor' }
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-2 text-sm font-medium ${
              activeTab === tab.key
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex space-x-4">
        <div>
          <label className="block text-sm font-medium mb-1">Tier</label>
          <select 
            value={selectedTier} 
            onChange={(e) => setSelectedTier(e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700"
          >
            <option value="">All Tiers</option>
            <option value="tier1">Tier 1 (Levels 1)</option>
            <option value="tier2">Tier 2 (Levels 2-4)</option>
            <option value="tier3">Tier 3 (Levels 5-7)</option>
            <option value="tier4">Tier 4 (Levels 8-10)</option>
          </select>
        </div>

        {activeTab === 'primary' && (
          <div>
            <label className="block text-sm font-medium mb-1">Type</label>
            <select 
              value={selectedType} 
              onChange={(e) => setSelectedType(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700"
            >
              <option value="">All Types</option>
              <option value="physical">Physical</option>
              <option value="magic">Magic</option>
            </select>
          </div>
        )}
      </div>

      {/* Items Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-96 overflow-y-auto">
        {filteredItems.map(item => 
          activeTab === 'armor' ? renderArmorCard(item) : renderWeaponCard(item)
        )}
      </div>

      {filteredItems.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          No equipment found matching the selected criteria.
        </div>
      )}

      {/* Item Detail Modal */}
      {selectedItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg max-w-md w-full mx-4">
            <h3 className="text-xl font-bold mb-4">{selectedItem.name}</h3>
            
            <div className="space-y-2 mb-4">
              {selectedItem.trait && <p><strong>Trait:</strong> {selectedItem.trait}</p>}
              {selectedItem.range && <p><strong>Range:</strong> {selectedItem.range}</p>}
              {selectedItem.damage && <p><strong>Damage:</strong> {selectedItem.damage}</p>}
              {selectedItem.burden && <p><strong>Burden:</strong> {selectedItem.burden}</p>}
              {selectedItem.baseThresholds && (
                <p><strong>Base Thresholds:</strong> {selectedItem.baseThresholds.major} / {selectedItem.baseThresholds.severe}</p>
              )}
              {selectedItem.baseScore && <p><strong>Base Score:</strong> {selectedItem.baseScore}</p>}
              {selectedItem.type && <p><strong>Type:</strong> {selectedItem.type}</p>}
              {selectedItem.requiresSpellcast && (
                <p className="text-purple-600 dark:text-purple-400"><strong>Requires Spellcast</strong></p>
              )}
            </div>

            {selectedItem.feature && (
              <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded mb-4">
                <strong>Feature:</strong> {selectedItem.feature}
              </div>
            )}

            <div className="flex space-x-2">
              <button 
                onClick={() => addToInventory(selectedItem)}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
              >
                Add to Inventory
              </button>
              <button 
                onClick={() => setSelectedItem(null)}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EquipmentBrowser;
